import { cur } from '@/lib/currency';
import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TrendingUp, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';

/**
 * Reconciliation Check banner — auto-loads silently on mount, shows
 * green/warning state plus on-demand drill-down. Extracted from
 * DashboardPage.jsx to keep that file under 700 LOC.
 */
export function ReconciliationCheckWidget() {
  const [state, setState] = useState({ loading: false, data: null, error: null });
  const [expanded, setExpanded] = useState(false);

  const run = async (silent = false) => {
    if (!silent) setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const r = await api.get('/admin/reconciliation-check');
      setState({ loading: false, data: r.data, error: null });
    } catch (e) {
      setState({ loading: false, data: null, error: e.response?.data?.detail || 'Check failed' });
    }
  };

  useEffect(() => { run(true); }, []);

  const divergenceCount = state.data?.divergences?.length ?? 0;
  const healthy = state.data?.is_healthy;
  const tone = healthy ? 'success' : divergenceCount > 0 ? 'warning' : 'muted';

  if (state.error) {
    return (
      <Card className="border-border bg-muted/20"><CardContent className="p-3 text-xs text-muted-foreground flex items-center justify-between">
        <span>Reconciliation Check unavailable: {state.error}</span>
        <Button size="sm" variant="ghost" onClick={() => run(false)} className="h-7 text-xs">Retry</Button>
      </CardContent></Card>
    );
  }

  return (
    <Card className={`border-2 ${healthy ? 'border-success/30 bg-success/5' : divergenceCount > 0 ? 'border-warning/30 bg-warning/5' : 'border-border bg-muted/10'}`} data-testid="reconciliation-widget">
      <CardContent className="p-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${tone === 'success' ? 'bg-success/15 text-success' : tone === 'warning' ? 'bg-warning/15 text-warning' : 'bg-muted text-muted-foreground'}`}>
            {healthy ? <TrendingUp size={16} /> : <AlertTriangle size={16} />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-foreground">
              Reconciliation Check{state.data?.period && <span className="text-muted-foreground font-normal"> · {state.data.period.start_date} → {state.data.period.end_date}</span>}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {state.loading ? 'Scanning ledgers…' : healthy ? 'All totals reconcile across reports' : `${divergenceCount} divergence${divergenceCount !== 1 ? 's' : ''} found — click to review`}
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={() => run(false)} disabled={state.loading} className="h-8 text-xs" data-testid="reconciliation-refresh-btn">
            {state.loading ? 'Checking…' : 'Re-check'}
          </Button>
          {state.data && (
            <Button size="sm" variant={expanded ? 'default' : 'ghost'} onClick={() => setExpanded(!expanded)} className="h-8 text-xs" data-testid="reconciliation-expand-btn">
              {expanded ? 'Hide Details' : 'Show Details'}
            </Button>
          )}
        </div>

        {expanded && state.data && (
          <div className="mt-3 pt-3 border-t border-border space-y-3 text-xs" data-testid="reconciliation-details">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {Object.entries(state.data.totals || {}).map(([k, v]) => (
                <div key={k} className="p-2 rounded-lg bg-background border border-border">
                  <p className="text-[9px] uppercase tracking-wider text-muted-foreground">{k.replace(/_/g, ' ')}</p>
                  <p className="font-bold text-foreground tabular-nums">{cur()} {(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                </div>
              ))}
            </div>
            {divergenceCount > 0 ? (
              <div className="space-y-1.5">
                {state.data.divergences.map((c, i) => (
                  <div key={i} className={`p-2.5 rounded-lg border ${c.severity === 'error' ? 'border-error/30 bg-error/5' : 'border-warning/30 bg-warning/5'}`}>
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-foreground text-xs">{c.label}</p>
                      <span className={`tabular-nums font-bold ${c.severity === 'error' ? 'text-error' : 'text-warning'}`}>
                        {cur()} {Math.abs(c.diff).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {c.left?.toLocaleString()} vs {c.right?.toLocaleString()} — {c.hint}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-2.5 rounded-lg border border-success/30 bg-success/10 text-center text-success font-semibold">
                ✓ All totals match across Sales, Expenses, Bills, and Journal Entries
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ReconciliationCheckWidget;
